import {
    useEffect, useMemo, useRef, useContext,
    useCallback, useState,
} from 'react';
import { Badge, Progress } from 'reactstrap';
import cls from 'classnames';
import {
    Map, AdvancedMarker, useMap,
} from '@vis.gl/react-google-maps';
import { isEmpty } from 'lodash-es';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { faMapMarkerAlt, faTruck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Hide } from '@s20/components/common/Hide';
import { httpClient } from '@s20/services/httpClient';
import { GoogleSearch } from '../../../common/Map/GoogleSearch';
import { FormContext, IconButton } from '../../../common/forms/FormElements';
import { MAX_ZOOM, useClusters } from '../../../common/Map/useClusters';
import { ActionSheet } from '../../../common/ActionSheet';
import { ApiRoutes } from '../../../Common';
import { INTERPUNCT } from '../../../common/util';
import { InfoPane } from './InfoPane';
import { useJobQuery } from './useJobQuery';

const DEFAULT_ZOOM = 4;
const DEFAULT_US_CENTER = { lat: 39.8283, lng: -98.5795 };

export const MapView = ({ dispatchLocations, mapId }) => {
    const { setFieldValue, values } = useContext(FormContext);
    const { jobGroup, dispatchOfficeLocationId } = values;

    // Cleared as null, not undefined, so a destructuring default would not catch it.
    const selectedJob = values.selectedJob || {};

    const [bounds, setBounds] = useState(null);
    const [zoom, setZoom] = useState(DEFAULT_ZOOM);

    const { data: jobs } = useJobQuery();
    const map = useMap();

    const lastDispatchCenterRef = useRef(null);
    const [searchCenter, setSearchCenter] = useState(null);

    const [clusters, supercluster] = useClusters(jobs, bounds, zoom);

    const handlePlaceSelect = useCallback((place) => {
        if (!place) {
            setSearchCenter(null);
            return;
        }

        const loc = place.location;
        if (!loc) {
            return;
        }

        const newCenter = { lat: loc.lat(), lng: loc.lng(), place };
        setSearchCenter(newCenter);

        if (map) {
            map.panTo({ lat: newCenter.lat, lng: newCenter.lng });
            map.setZoom(DEFAULT_ZOOM);
        }
    }, [map]);

    const handleClusterClick = useCallback((latLng, clusterId) => {
        const expansionZoom = Math.min(
            supercluster.getClusterExpansionZoom(clusterId),
            MAX_ZOOM,
        );

        map.setZoom(expansionZoom);
        map.setCenter(latLng);
    }, [supercluster, map]);

    const dispatchCenter = useMemo(() => {
        const location = dispatchLocations?.find((loc) => loc.value === dispatchOfficeLocationId);
        const lat = location?.latitude ?? location?.Latitude ?? null;
        const lng = location?.longitude ?? location?.Longitude ?? null;

        if (lat == null || lng == null) {
            return null;
        };

        const latNum = Number(lat);
        const lngNum = Number(lng);

        if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) {
            return null;
        };

        return { lat: latNum, lng: lngNum };
    }, [dispatchLocations, dispatchOfficeLocationId]);

    const { data: equipment, isLoading: equipmentLoading } = useQuery({
        enabled: !!selectedJob.jobId,
        queryKey: ['equipmentForJob', selectedJob.assignmentDate, selectedJob.jobId],
        queryFn: () => httpClient.js(ApiRoutes.job.equipmentForJob(moment(selectedJob.assignmentDate).format('YYYY-MM-DD'), selectedJob.jobId)),
    });

    useEffect(() => {
        if (!map || !dispatchCenter) {
            return;
        }

        const isSameCenter = lastDispatchCenterRef.current
            && lastDispatchCenterRef.current.lat === dispatchCenter.lat
            && lastDispatchCenterRef.current.lng === dispatchCenter.lng;

        if (isSameCenter) {
            return;
        }

        lastDispatchCenterRef.current = dispatchCenter;

        map.panTo(dispatchCenter);
        map.setZoom(DEFAULT_ZOOM);
    }, [map, dispatchCenter]);

    return (
        <>
            <Progress className={cls(equipmentLoading ? 'mt-3' : 'invisible')} />

            <div className={cls('d-flex flex-fill flex-nowrap', !equipmentLoading && 'mt-3')}>
                <Map
                    mapId={mapId}
                    defaultCenter={dispatchCenter || DEFAULT_US_CENTER}
                    defaultZoom={DEFAULT_ZOOM}
                    gestureHandling="greedy"
                    mapTypeControl={false}
                    streetViewControl={false}
                    fullscreenControl={false}
                    zoomControl={false}
                    scaleControl={false}
                    clickableIcons={false}
                    cameraControl={false}
                    onBoundsChanged={(e) => {
                        setBounds(e.detail.bounds);
                    }}
                    onZoomChanged={(e) => {
                        setZoom(e.detail.zoom);
                    }}
                >
                    <GoogleSearch
                        onPlaceSelect={handlePlaceSelect}
                        selectedPlace={searchCenter?.place}
                    />

                    {clusters.map((cluster) => {
                        const [lng, lat] = cluster.geometry.coordinates;
                        const {
                            cluster: isCluster,
                            sum,
                        } = cluster.properties;

                        if (isCluster) {
                            return (
                                <AdvancedMarker
                                    key={`cluster-${cluster.id}`}
                                    position={{ lat, lng }}
                                    title={`${sum} jobs at this location`}
                                    className="cluster-marker"
                                >
                                    <IconButton
                                        onClick={() => handleClusterClick({ lat, lng }, cluster.id)}
                                    >
                                        {sum}
                                    </IconButton>
                                </AdvancedMarker>
                            );
                        }

                        const { jobs, objectID: key } = cluster.properties;

                        if (jobs?.length > 1) {
                            const hasSelectedJob = jobs.some((job) => job.jobId === selectedJob.jobId);

                            return (
                                <AdvancedMarker
                                    key={`cluster-jobs-${key}`}
                                    position={{ lat, lng }}
                                    title={`${jobs.length} jobs at this location`}
                                    onClick={() => {
                                        setFieldValue('jobGroup', jobs);
                                    }}
                                    className="marker-group"
                                >
                                    <div>
                                        <FontAwesomeIcon
                                            icon={faMapMarkerAlt}
                                            size="2x"
                                            className={cls('map-marker', 'text-danger', hasSelectedJob && 'map-marker-selected')}
                                        />

                                        <Badge
                                            color="danger"
                                            className="shadow border"
                                            pill
                                        >
                                            {jobs.length}
                                        </Badge>
                                    </div>
                                </AdvancedMarker>
                            );
                        }

                        const [job] = jobs;

                        return (
                            <AdvancedMarker
                                key={`job-${key}`}
                                position={{ lat, lng }}
                                title={`${job.customer || ''} - ${job.jobNumber}`}
                                onClick={() => {
                                    setFieldValue('selectedJob', job);
                                    setFieldValue('jobGroup', jobs);
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faMapMarkerAlt}
                                    size="2x"
                                    className={cls('map-marker', 'text-danger', selectedJob.jobId === job.jobId && 'map-marker-selected')}
                                />
                            </AdvancedMarker>
                        );
                    })}

                    {(equipment || []).map((vehicle) => (
                        <AdvancedMarker
                            key={`job-${vehicle.equipmentId}`}
                            position={{ lat: vehicle.latitude, lng: vehicle.longitude }}
                            title={`${vehicle.equipmentIdentifier} ${INTERPUNCT} ${vehicle.equipmentTypeName}`}
                        >
                            <FontAwesomeIcon
                                icon={faTruck}
                                size="2x"
                                className={cls('map-marker text-light')}
                            />
                        </AdvancedMarker>
                    ))}
                </Map>

                <Hide mdUp>
                    <div className="flex-none">
                        <InfoPane />
                    </div>
                </Hide>

            </div>

            <ActionSheet
                isOpen={!isEmpty(jobGroup)}
                onClose={() => {
                    setFieldValue('selectedJob', null);
                    setFieldValue('jobGroup', null);
                }}
            >
                <InfoPane />
            </ActionSheet>
        </>
    );
};
